import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { useContactDrawer } from "../context/ContactDrawerContext";
import { opgaverData, galleriSubpages } from "../data/siteData";

export default function Home() {
  const { openDrawer } = useContactDrawer();
  const [services, setServices] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [activeGallery, setActiveGallery] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [isLoaded, setIsLoaded] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);

  useEffect(() => {
    setIsLoaded(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 2500);

    const handleScroll = () => {
      if (window.scrollY < 1200) {
        setScrollY(window.scrollY);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    // Fetch services from CMS
    fetch("https://api.dev.auxo.dk/public/content/services")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setServices(data);
      })
      .catch((err) => console.error("Error fetching services:", err));

    // Fetch galleries
    fetch("https://api.dev.auxo.dk/public/content/galleries")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setGalleries(data);
          if (data.length > 0) {
            setActiveGallery(data[0]);
          }
        }
      })
      .catch((err) => console.error("Error fetching galleries:", err));
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxImage || !activeGallery) return;
      if (e.key === "Escape") {
        setLightboxImage(null);
      } else if (e.key === "ArrowRight") {
        const nextIdx = (lightboxIndex + 1) % activeGallery.images.length;
        setLightboxIndex(nextIdx);
        setLightboxImage(activeGallery.images[nextIdx]);
      } else if (e.key === "ArrowLeft") {
        const prevIdx = (lightboxIndex - 1 + activeGallery.images.length) % activeGallery.images.length;
        setLightboxIndex(prevIdx);
        setLightboxImage(activeGallery.images[prevIdx]);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxImage, lightboxIndex, activeGallery]);

  return (
    <div className="pt-[72px]">
      <SEO
        title="Anlægsgartner i Hobro, Arden & Hadsund | Få et stærkt tilbud"
        description="Din professionelle anlægsgartner til belægningsopgaver, lægning af indkørsel og terrasse, samt opsætning af støttemure. Få et tilbud"
        ogImage="https://assets.auxodev.dk/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/elmegaarden-0003-0.jpg"
        canonical="/"
      />

      {/* 1. Hero Section */}
      <section className="relative min-h-[55vh] lg:min-h-[62vh] flex items-center bg-brand-green-950 overflow-hidden pt-32 pb-14 lg:pt-40 lg:pb-24">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/645a442217253.jpeg"
            alt="Din anlægsgartner i Nordjylland"
            className="w-full h-full object-cover origin-center"
            style={{
              transform: `scale(${isLoaded ? 1.08 + scrollY * 0.0004 : 1.18}) translateY(${scrollY * 0.1}px)`,
              transition: isTransitioning ? "transform 2.5s cubic-bezier(0.16, 1, 0.3, 1)" : "none",
              willChange: "transform",
            }}
          />
          {/* Dark gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/45 z-10"></div>
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: Content */}
            <div className="lg:col-span-7 flex flex-col gap-6 text-white text-left">
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] text-white">
                Din anlægsgartner i Nordjylland
              </h1>
              <p className="text-base md:text-lg text-neutral-200 max-w-xl leading-relaxed">
                Vi har mange års erfaring inden for anlæg af have. Vi kan også hjælpe dig!
              </p>

              <div className="flex flex-wrap gap-3 mt-2">
                <button
                  onClick={openDrawer}
                  className="px-4 py-2.5 sm:px-6 sm:py-3.5 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-xs sm:text-sm tracking-wide rounded-sm transition-all duration-300 shadow-sm cursor-pointer"
                >
                  Få et tilbud
                </button>
                <Link
                  to="/projekter"
                  className="px-4 py-2.5 sm:px-6 sm:py-3.5 bg-white hover:bg-neutral-100 text-brand-green-950 font-bold text-xs sm:text-sm tracking-wide rounded-sm transition-all duration-300 cursor-pointer shadow-sm"
                >
                  Se vores galleri
                </Link>
                <a
                  href="tel:+4520661517"
                  className="px-4 py-2.5 sm:px-6 sm:py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold text-xs sm:text-sm tracking-wide rounded-sm transition-all duration-300 flex items-center gap-2 cursor-pointer shadow-sm border border-white/20"
                >
                  <i className="fa-solid fa-phone text-[#7ca632]" /> +45 20 66 15 17
                </a>
              </div>
            </div>

            {/* Right Column: Core Values Vertical List */}
            <div className="lg:col-span-5 w-full flex flex-col gap-4">
              <div className="bg-black/40 backdrop-blur-xs border border-white/10 p-6 md:p-8 rounded-sm flex flex-col gap-4 text-left">
                <h3 className="font-display text-xl font-bold text-white mb-2">
                  Vi udfører:
                </h3>
                <ul className="space-y-3 text-neutral-200 text-sm md:text-base font-medium">
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-check text-[#7ca632]" />
                    <span>Anlægsgartner & haveanlæg</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-check text-[#7ca632]" />
                    <span>Terrasse (opbygning & sten)</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-check text-[#7ca632]" />
                    <span>Indkørsel (lægning & renovering)</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-check text-[#7ca632]" />
                    <span>Belægning & brolægning</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-check text-[#7ca632]" />
                    <span>Støttemur opbygning</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-check text-[#7ca632]" />
                    <span>Autoriseret kloakmester & dræn</span>
                  </li>
                </ul>
                <div className="mt-2 pt-4 border-t border-white/10">
                  <span className="text-xs text-neutral-300">... og mange andre opgaver!</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Kompetent Anlægsgartner sektion */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Billede i venstre side */}
          <div className="lg:col-span-6 relative order-2 lg:order-1">
            <div className="relative overflow-hidden rounded-sm border border-brand-sand-300 shadow-md">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,height=600,fit=cover,quality=85,format=auto/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/img-2196.jpeg"
                alt="Kompetent anlægsgartner i Hobro, Arden & Hadsund"
                className="w-full h-[380px] md:h-[480px] object-cover"
              />
            </div>
          </div>

          {/* Tekst i højre side */}
          <div className="lg:col-span-6 flex flex-col gap-6 order-1 lg:order-2 text-left">
            <div className="flex flex-col gap-3">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-green-900 leading-tight">
                Kompetent anlægsgartner i Hobro, Arden & Hadsund
              </h2>
            </div>
            
            <div className="flex flex-col gap-5 text-brand-charcoal/85 leading-relaxed text-base">
              <p className="font-medium text-brand-green-950 text-lg">
                Din professionelle anlægsgartner til belægningsopgaver, lægning af indkørsel og terrasse, samt opsætning af støttemure. Vi servicere primært kunder fra Hobro, Arden, Hadsund & resten af Nordjylland.
              </p>
              <hr className="border-brand-sand-200" />
              <p>
                Har du brug for en nærværende og kompetent anlægsgartner, som har flere års erfaring på bagen, så er du kommet det rette sted hen. Elmegaarden Have og Anlæg har mange års erfaring som anlægsgartner for private og erhverv.
              </p>
              <div>
                <h3 className="font-display text-xl font-bold text-brand-green-900 mb-2">
                  Anlægsgartner med øje for detaljerne
                </h3>
                <p>
                  Vi udfører alt inden for anlægsarbejde såsom anlæggelse af ny have, bede, træer, buske og meget andet. Ved vedligeholdelse af grønne områder udfører vi græsslåning, beskæring af buske, fældning af træer m.m.
                </p>
              </div>
              <p className="font-semibold text-brand-green-900">
                Uanset hvad du skal bruge en anlægsgartner til - så kontakt os på{" "}
                <a href="tel:20661517" className="text-[#7ca632] underline hover:text-[#6b9128]">
                  20 66 15 17
                </a>
              </p>
              <div className="mt-2 flex flex-wrap gap-4">
                <button
                  onClick={openDrawer}
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-xs sm:text-sm tracking-wide uppercase rounded-sm transition-all duration-300 cursor-pointer shadow-sm group"
                >
                  Få et tilbud
                  <i className="fa-solid fa-arrow-right text-xs transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-brand-sand-100 hover:bg-brand-sand-200 text-brand-green-900 font-bold text-xs sm:text-sm tracking-wide uppercase rounded-sm transition-all duration-300 border border-brand-sand-300"
                >
                  Kontakt os
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Opgaver & Ydelser Grids */}
      <section className="py-20 bg-brand-sand-100 border-t border-b border-brand-sand-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-left max-w-3xl flex flex-col gap-4 mb-14">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-green-900">
              Vores primære opgaver
            </h2>
            <p className="text-brand-charcoal/70">
              Vi står for en lang række af anlægsrelaterede opgaver for dig:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {opgaverData.map((opgave) => (
              <Link
                key={opgave.slug}
                to={`/opgaver/${opgave.slug}`}
                className="group bg-white border border-brand-sand-200 rounded-sm overflow-hidden shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between text-left"
              >
                {opgave.galleryImages?.[0] && (
                  <div className="aspect-16/10 overflow-hidden bg-neutral-100">
                    <img
                      src={opgave.galleryImages[0].src.replace(
                        "https://assets.auxodev.dk/assets/",
                        "https://assets.auxodev.dk/cdn-cgi/image/width=600,height=375,fit=cover,quality=80,format=auto/assets/"
                      )}
                      alt={opgave.heading}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-6 flex flex-col gap-3 flex-grow justify-between">
                  <div>
                    <h3 className="font-display text-lg font-bold text-brand-green-900 group-hover:text-[#7ca632] transition-colors">
                      {opgave.heading}
                    </h3>
                    <p className="text-xs text-brand-charcoal/70 line-clamp-3 mt-2 leading-relaxed">
                      {opgave.metaDescription}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#7ca632] mt-4">
                    Læs mere <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}

            {/* Andre opgaver kort */}
            <Link
              to="/andre-opgaver"
              className="group bg-white border border-brand-sand-200 rounded-sm overflow-hidden shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between text-left"
            >
              <div className="aspect-16/10 overflow-hidden bg-neutral-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=375,fit=cover,quality=80,format=auto/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/645a442217253_ae6bb7e0.jpeg"
                  alt="Andre opgaver"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-6 flex flex-col gap-3 flex-grow justify-between">
                <div>
                  <h3 className="font-display text-lg font-bold text-brand-green-900 group-hover:text-[#7ca632] transition-colors">
                    Andre opgaver
                  </h3>
                  <p className="text-xs text-brand-charcoal/70 line-clamp-3 mt-2 leading-relaxed">
                    Vi kan lave alt inden for have og anlæg. Du er velkommen til at ringe til os og få et tilbud.
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#7ca632] mt-4">
                  Læs mere <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. CMS Ydelser (inkl. Autoriseret Kloakarbejde) */}
      {services.length > 0 && (
        <section className="py-20 bg-brand-sand-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-left max-w-3xl flex flex-col gap-4 mb-14">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-green-900">
                Specialer & autoriseret kloakmester
              </h2>
              <p className="text-brand-charcoal/70">
                Som autoriseret kloakmestervirksomhed under Byggeriets Kvalitetskontrol sikrer vi altid godkendt kvalitet.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <Link
                  key={service.id}
                  to={`/vi-tilbyder/${service.slug?.da || service.slug}`}
                  className="group bg-white border border-brand-sand-200 rounded-sm overflow-hidden shadow-xs hover:shadow-md transition-all duration-300 flex flex-col text-left justify-between"
                >
                  <div className="h-52 overflow-hidden bg-neutral-100">
                    <img
                      src={service.image?.[0]?.replace(
                        "https://assets.auxodev.dk/assets/",
                        "https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/assets/"
                      )}
                      alt={service.title?.da || service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 flex flex-col gap-3 flex-grow justify-between">
                    <div>
                      <h3 className="font-display text-lg font-bold text-brand-green-900 group-hover:text-[#7ca632] transition-colors">
                        {service.title?.da || service.title}
                      </h3>
                      <p className="text-xs text-brand-charcoal/70 line-clamp-3 mt-2 leading-relaxed">
                        {service.short_description?.da || service.short_description}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#7ca632] mt-4">
                      Læs mere <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 5. Galleri sektioner fra gammel side */}
      <section className="py-20 bg-white border-t border-brand-sand-200">
        <div className="max-w-7xl mx-auto px-6 text-left">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-green-900">
                Gallerier & udvalgte projekter
              </h2>
              <p className="text-brand-charcoal/70 mt-2">
                Se billeder fra vores arbejde med terrasser, indkørsler, støttemure og fliselægning.
              </p>
            </div>
            <Link
              to="/projekter"
              className="px-6 py-3 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-xs uppercase tracking-wider rounded-sm transition-colors shadow-sm shrink-0"
            >
              Se hele galleriet
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {galleriSubpages.map((sub) => (
              <Link
                key={sub.slug}
                to={`/galleri/${sub.slug}`}
                className="group relative aspect-square rounded-sm overflow-hidden bg-neutral-100 border border-brand-sand-200 shadow-xs hover:shadow-md transition-all"
              >
                <img
                  src={sub.images[0].src.replace(
                    "https://assets.auxodev.dk/assets/",
                    "https://assets.auxodev.dk/cdn-cgi/image/width=400,height=400,fit=cover,quality=80,format=auto/assets/"
                  )}
                  alt={sub.heading}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-4 text-white">
                  <span className="font-display text-sm font-bold leading-snug">
                    {sub.heading}
                  </span>
                  <span className="text-[11px] text-neutral-300 mt-1 flex items-center gap-1">
                    Se mere <i className="fa-solid fa-arrow-right text-[8px] group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && activeGallery && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all duration-300 cursor-pointer z-50"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark text-xl" />
          </button>

          {activeGallery.images?.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const prevIdx = (lightboxIndex - 1 + activeGallery.images.length) % activeGallery.images.length;
                setLightboxIndex(prevIdx);
                setLightboxImage(activeGallery.images[prevIdx]);
              }}
              className="absolute left-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all duration-300 cursor-pointer z-50"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left text-lg" />
            </button>
          )}

          {activeGallery.images?.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const nextIdx = (lightboxIndex + 1) % activeGallery.images.length;
                setLightboxIndex(nextIdx);
                setLightboxImage(activeGallery.images[nextIdx]);
              }}
              className="absolute right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all duration-300 cursor-pointer z-50"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right text-lg" />
            </button>
          )}

          <div 
            className="relative max-w-5xl max-h-[80vh] px-4 flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.replace(
                "https://assets.auxodev.dk/assets/",
                "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/"
              )}
              alt="Galleri visning"
              className="max-w-full max-h-[75vh] object-contain rounded-sm shadow-2xl"
            />
            <div className="mt-4 text-center">
              <p className="text-white font-bold text-lg">
                {activeGallery.title?.da?.replace(" - Galleri", "") || "Galleri"}
              </p>
              <p className="text-neutral-400 text-sm mt-1">
                Billede {lightboxIndex + 1} af {activeGallery.images?.length || 1}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
